import {useCallback,useEffect,useRef,useState} from "react";
import {
  Alert,Box,Button,Checkbox,Chip,CircularProgress,Dialog,DialogActions,
  DialogContent,DialogTitle,Divider,MenuItem,Paper,Stack,TextField,Typography
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import "./MissionWorkspace.css";

const STATUS={
  open:{label:"Needs approval",color:"info"},
  preparing:{label:"Preparing",color:"default"},
  processing:{label:"In progress",color:"info"},
  "needs-review":{label:"Needs investigation",color:"warning"},
  resolved:{label:"Prepared",color:"success"},
  cancelled:{label:"Cancelled",color:"default"},
};
function safeRecordLink(path,instance){
  if(typeof path!=="string"||!path.startsWith("/instance/"+instance+"/")||
    path.includes("..")||path.includes("//"))return null;
  return path;
}
function missionFetch(path,options={}){
  return fetch(process.env.REACT_APP_NODEJS_SERVER_URL+path,{
    ...options,
    headers:{
      accessToken:localStorage.getItem("accessToken"),
      ...(options.body?{"Content-Type":"application/json"}:{}),
      ...(options.headers||{}),
    },
  }).then(async response=>{
    const payload=await response.json().catch(()=>({}));
    if(!response.ok||!payload.success)throw new Error(payload.message||"Unable to load Ezee Mission.");
    return payload;
  });
}
const formatDate=value=>{
  const date=new Date(value);
  return Number.isNaN(date.getTime())?"":date.toLocaleString();
};
const money=(n,currency="CAD")=>{
  if(n===null||n===undefined||n==="")return "Price unavailable";
  const value=Number(n);
  if(!Number.isFinite(value))return "Price unavailable";
  return value.toLocaleString("en-CA",{style:"currency",
    currency:["CAD","USD"].includes(currency)?currency:"CAD"});
};
const stageLabel=(stage,documentLabel="Presentation")=>({
  cart:"Update project cart",
  presentation:"Generate presentation",
  estimate:"Generate estimate",
  "sales-order":"Generate sales order",
  email_draft_ready:documentLabel+" prepared",
})[stage]||"Review mission";

export default function MissionWorkspace({instanceUniqueId,compact=false,onStartChat}){
  const [missions,setMissions]=useState([]);
  const [selected,setSelected]=useState(null);
  const [activeId,setActiveId]=useState(null);
  const [busy,setBusy]=useState(false);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");
  const [newDocumentType,setNewDocumentType]=useState("presentation");
  const [approvalOpen,setApprovalOpen]=useState(false);
  const [ackFinancial,setAckFinancial]=useState(false);
  const [cancelOpen,setCancelOpen]=useState(false);
  const requestEpoch=useRef(0);
  const base="/ai/instances/"+encodeURIComponent(instanceUniqueId||"")+"/missions";

  const refresh=useCallback(async(currentId=null)=>{
    if(!instanceUniqueId)return;
    const epoch=++requestEpoch.current;
    setLoading(true);setError("");
    try{
      const response=await missionFetch(base);
      if(epoch!==requestEpoch.current)return;
      const items=response.missions||[];
      setMissions(items);
      const requestedId=currentId&&(items.some(item=>Number(item.id)===Number(currentId)))
        ?currentId:(items[0]?.id||null);
      if(requestedId){
        const detail=await missionFetch(base+"/"+encodeURIComponent(requestedId));
        if(epoch!==requestEpoch.current)return;
        setSelected(detail.mission);setActiveId(requestedId);
      }else{
        setSelected(null);setActiveId(null);
      }
    }catch(cause){
      if(epoch===requestEpoch.current)setError(cause.message||"Unable to load missions.");
    }finally{
      if(epoch===requestEpoch.current)setLoading(false);
    }
  },[base,instanceUniqueId]);
  useEffect(()=>{
    setMissions([]);setSelected(null);setActiveId(null);
    refresh().catch(()=>{});
    return ()=>{requestEpoch.current+=1;};
  },[refresh]);
  const select=async id=>{
    setApprovalOpen(false);setAckFinancial(false);setCancelOpen(false);
    const epoch=++requestEpoch.current;
    setActiveId(id);setSelected(null);setError("");setLoading(false);
    try{
      const response=await missionFetch(base+"/"+encodeURIComponent(id));
      if(epoch===requestEpoch.current)setSelected(response.mission);
    }catch(cause){
      if(epoch===requestEpoch.current)setError(cause.message||"Unable to open mission.");
    }
  };
  const act=async action=>{
    if(!selected||busy||(action==="approve"&&selected.requiresFinancialAcknowledgement&&!ackFinancial))return;
    setBusy(true);setError("");setApprovalOpen(false);setCancelOpen(false);
    const id=selected.workflowId;
    try{
      await missionFetch(base+"/"+encodeURIComponent(id)+"/"+action,{
        method:"POST",
        body:JSON.stringify(action==="approve"?{
          confirm:true,step:selected.step,
          ...(selected.requiresFinancialAcknowledgement?{acknowledgeFinancialSideEffects:ackFinancial}:{})
        }:{confirm:true}),
      });
      await refresh(id);
      setAckFinancial(false);
    }catch(cause){
      const message=cause.message||"Mission action was not completed.";
      try{await refresh(id);}catch(_ignored){}
      setError(message);
    }finally{setBusy(false);}
  };
  const status=STATUS[selected?.state]||{label:selected?.state||"Unknown",color:"default"};
  const pending=selected?.state==="open"&&selected?.requiresConfirmation;
  const documentLabel=selected?.documentLabel||"Presentation";
  const newDocumentLabel={
    presentation:"Presentation",estimate:"Estimate","sales-order":"Sales Order",
  }[newDocumentType]||"Presentation";
  const isSalesOrder=selected?.documentType==="sales-order";
  const isEstimate=selected?.documentType==="estimate";
  const currentProduct=selected?.stage==="cart"?
    (selected.products||[])[Math.max(0,(selected.step||1)-1)]:null;
  const stages=[
    ...(selected?.products||[]).map((p,i)=>({
      key:"product-"+i,label:"Add "+(p.name||"product "+(i+1)),
      detail:(p.quantity||0)+" units · "+(p.source||"supplier"),
      done:selected.step>i+1||selected.state==="resolved",
      current:selected.step===i+1&&pending,
    })),
    {key:"document",label:"Create "+documentLabel,
      detail:isSalesOrder?"May also create a deposit invoice, depending on payment terms":
        "Use the project's authoritative cart",
      done:selected?.state==="resolved",current:selected?.stage===selected?.documentType&&pending},
    {key:"email",label:"Prepare email draft",
      detail:"Not sent automatically",
      done:selected?.state==="resolved",current:false},
  ];
  const progress=selected?.state==="resolved"?100:Math.min(95,
    Math.max(0,((Math.max(0,(selected?.step||1)-1))/(selected?.totalSteps||1))*100));
  const documentLink=safeRecordLink(selected?.document?.source?.url,instanceUniqueId);
  return <Box className={"ezee-missions "+(compact?"ezee-missions--compact":"")}>
    <Box className="ezee-missions__heading">
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
          <AutoAwesomeIcon sx={{fontSize:20,color:"#b91c1c"}}/>
          <Typography variant="h6" fontWeight={800}>Missions</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          What Ezee is working on, what needs approval, and what it finished.
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
        {onStartChat&&<>
          <TextField size="small" select label="New mission" sx={{minWidth:150}}
            value={newDocumentType}
            onChange={event=>setNewDocumentType(event.target.value)}>
            <MenuItem value="presentation">Presentation</MenuItem>
            <MenuItem value="estimate">Estimate</MenuItem>
            <MenuItem value="sales-order">Sales Order</MenuItem>
          </TextField>
          <Button variant="contained" size="small" onClick={()=>
            onStartChat("Help me prepare a "+newDocumentLabel+
              " mission in Elite Core. Ask me for the project and selected products and quantities if missing. "+
              (newDocumentType==="estimate"?
                "For each product, confirm colour/size, target margin and any imprint method, location and verified cost. Use separate lines for different sizes or colours. ":
                "")+
              "Use the existing Sales Document Mission workflow, show approvals and do not send any customer emails.")
          }>Plan in chat</Button>
        </>}
        <Button size="small" variant="outlined" disabled={busy||loading}
          startIcon={loading?<CircularProgress size={14}/>:<RefreshIcon/>}
          onClick={()=>void refresh(activeId)}>Refresh</Button>
      </Stack>
    </Box>
    {error&&<Alert severity="error" sx={{mb:2}}>{error}</Alert>}
    <Box className="ezee-missions__layout">
      <Box className="ezee-missions__list">
        <Typography variant="overline" color="text.secondary" fontWeight={800}>My missions</Typography>
        {!loading&&!missions.length&&<Paper variant="outlined" sx={{p:2,borderRadius:2,mt:1}}>
          <Typography variant="body2" fontWeight={700}>Nothing in progress yet</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{mt:0.75}}>
            Ask Ezee to prepare a presentation, estimate or sales order. Missions use authorized Core capabilities.
          </Typography>
          {onStartChat&&<Button size="small" sx={{mt:1}} onClick={onStartChat}>Start in chat</Button>}
        </Paper>}
        <Stack spacing={1} sx={{mt:1}}>
          {missions.map(item=>{
            const active=Number(item.id)===Number(activeId);
            const tag=STATUS[item.state]||{label:item.state};
            return <button key={item.id} type="button"
              className={"ezee-missions__item "+(active?"ezee-missions__item--active":"")}
              onClick={()=>void select(item.id)} aria-pressed={active}>
              <span className="ezee-missions__item-title">{item.title}</span>
              <span className="ezee-missions__item-meta">
                <span>#{item.id}</span><span>{tag.label}</span>
              </span>
              <span className="ezee-missions__item-time">{formatDate(item.updatedAt)}</span>
            </button>;
          })}
        </Stack>
      </Box>
      <Paper className="ezee-missions__detail" variant="outlined">
        {!selected?<Box sx={{p:3,textAlign:"center"}}>
          {loading&&<CircularProgress size={24}/>}
          <Typography variant="body2" color="text.secondary" sx={{mt:1}}>
            {missions.length?"Select a mission to review.":"Your mission timeline and approvals will appear here."}
          </Typography>
        </Box>:<Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box>
              <Typography variant="caption" fontWeight={800} color="text.secondary">
                MISSION #{selected.workflowId} · PROJECT #{selected.projectId}
              </Typography>
              <Typography variant="h6" fontWeight={800}>
                {selected.projectName||"Project #"+selected.projectId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selected.customerName||"Customer sales document"}
              </Typography>
            </Box>
            <Chip label={status.label} color={status.color} size="small" variant="outlined"/>
          </Stack>
          <Box>
            <Stack direction="row" justifyContent="space-between" mb={0.75}>
              <Typography variant="caption" fontWeight={700}>{stageLabel(selected.stage,documentLabel)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {selected.state==="resolved"?"Completed":"Step "+selected.step+" of "+selected.totalSteps}
              </Typography>
            </Stack>
            <Box className="ezee-missions__progress"><Box sx={{width:progress+"%"}}/></Box>
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={800} mb={1}>Execution timeline</Typography>
            <Stack spacing={1.25}>
              {stages.map(stage=><Stack direction="row" spacing={1} key={stage.key} alignItems="flex-start">
                {stage.done?<CheckCircleOutlineIcon color="success" fontSize="small"/>:
                  stage.current?<PlayArrowOutlinedIcon color="info" fontSize="small"/>:
                    <Box className="ezee-missions__idle-dot"/>}
                <Box>
                  <Typography variant="body2" fontWeight={stage.current?800:600}>{stage.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{stage.detail}</Typography>
                </Box>
              </Stack>)}
            </Stack>
          </Box>
          <Divider/>
          <Box>
            <Typography variant="subtitle2" fontWeight={800} mb={1}>Prepared products</Typography>
            <Stack spacing={1}>
              {(selected.products||[]).map((p,i)=><Box className="ezee-missions__product" key={i}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography variant="body2" fontWeight={700}>{p.name||"Product "+(i+1)}</Typography>
                  <Chip size="small" variant="outlined" label={p.status==="completed"?"Added":p.status==="awaiting_confirmation"?"Review":"Queued"}
                    color={p.status==="completed"?"success":"default"}/>
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block">
                  {"Qty "+(p.quantity||0)+" · "+money(p.unitPrice,p.currency)+" each"}
                  {p.estimatedMargin!=null&&Number.isFinite(Number(p.estimatedMargin))?
                    " · "+Number(p.estimatedMargin).toFixed(1)+"% est. margin":""}
                </Typography>
                {isEstimate&&<Stack spacing={0.5} sx={{mt:1}}>
                  <Typography variant="caption" color="text.secondary">
                    {["Supplier: "+(p.supplierName||"Unspecified"),
                      p.sku?"SKU: "+p.sku:null,
                      p.color&&p.color!=="N/A"?"Colour: "+p.color:null,
                      p.size&&p.size!=="N/A"?"Size: "+p.size:null].filter(Boolean).join(" · ")}
                  </Typography>
                  <Typography variant="caption">
                    {"Unit net cost "+money(p.netCost,p.currency)+
                      " · Product "+money(p.productRevenue,p.currency)+
                      " · Decoration "+money(p.decorationRevenue,p.currency)}
                  </Typography>
                  {p.decoration&&<Box sx={{pl:1.5,borderLeft:"2px solid #bed8cd"}}>
                    <Typography variant="caption" fontWeight={700} display="block">
                      {"Imprint: "+(p.decoration.method||"Decoration")+
                        " · "+(p.decoration.location||"Location missing")}
                    </Typography>
                    {(p.decoration.charges||[]).map((charge,index)=>
                      <Typography key={index} variant="caption" display="block" color="text.secondary">
                        {(charge.name||"Charge")+" · Qty "+charge.quantity+
                          " · "+money(charge.price,p.currency)+" · Total "+money(charge.total,p.currency)}
                      </Typography>)}
                    <Typography variant="caption" color="text.secondary" display="block">
                      {"Proof: "+(p.decoration.proofRequired||"Review needed")}
                    </Typography>
                  </Box>}
                  <Typography variant="caption" fontWeight={800}>
                    {"Proposed line total: "+money(p.totalRevenue,p.currency)}
                  </Typography>
                  {p.convertedNetCostProvided&&<Alert severity="info" sx={{mt:0.5}}>
                    A converted net cost was supplied. Verify its source and rate.
                  </Alert>}
                </Stack>}
              </Box>)}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{mt:1}}>
              Prices are prepared estimates. Confirm product details and pricing before approving.
            </Typography>
          </Box>
          {isEstimate&&selected.documentPreview&&<Box className="ezee-missions__approval">
            <Typography variant="subtitle2" fontWeight={800}>Final Estimate review</Typography>
            <Typography variant="body2">
              {"Customer #"+(selected.documentPreview.customerId||"missing")+
                " · "+(selected.documentPreview.cartProductCount||0)+
                " total cart lines · "+(selected.documentPreview.currency||"CAD")}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {"In-hands date: "+(selected.documentPreview.inHandsDate||"Not set")}
            </Typography>
            <Stack spacing={0.5} sx={{mt:1}}>
              {(selected.documentPreview.cartProducts||[]).map((item,index)=>
                <Typography variant="caption" key={String(item.id||item.productId||index)+"-"+index}>
                  {(index+1)+". "+(item.name||"Unnamed product")+" · Qty "+(item.quantity||0)}
                </Typography>)}
            </Stack>
            {(selected.documentPreview.cartProductCount||0)>(selected.productCount||0)&&
              <Alert severity="warning" sx={{mt:1}}>
                The project cart includes other existing products. This estimate will use the entire cart.
              </Alert>}
            <Typography variant="caption" color="text.secondary" display="block" sx={{mt:1}}>
              Shipping, taxes and final document totals are calculated by Core, not included in this preview. Changes to the cart, customer or in-hands date will stop creation.
            </Typography>
          </Box>}
          {selected.state==="needs-review"&&<Alert severity="warning">
            A step may have partially changed the project. Ezee will not retry automatically. Inspect Core records before doing anything else.
          </Alert>}
          {selected.state==="processing"&&<Alert severity="info">
            A step is currently being processed. Refresh for its status; do not approve again.
          </Alert>}
          {selected.state==="cancelled"&&<Alert severity="info">
            This mission was cancelled. Previously completed Core changes, including any invoices, remain.
          </Alert>}
          {selected.state==="open"&&!pending&&<Box className="ezee-missions__approval">
            <Typography variant="body2">{selected.next}</Typography>
            <Button color="inherit" size="small" sx={{mt:1}} disabled={busy}
              onClick={()=>setCancelOpen(true)}>Cancel paused mission</Button>
          </Box>}
          {pending&&<Box className="ezee-missions__approval">
            <Typography variant="subtitle2" fontWeight={800}>Review required</Typography>
            <Typography variant="body2">
              {selected.stage==="cart"?
                "Approve the next product addition to the live project cart.":
                "Approve "+documentLabel+" creation from the current project cart."}
              {" "}This authorizes one step, never an entire workflow or email delivery.
            </Typography>
            <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
              <Button variant="contained" size="small" disabled={busy}
                onClick={()=>setApprovalOpen(true)}>Review & approve</Button>
              <Button color="inherit" size="small" disabled={busy}
                onClick={()=>setCancelOpen(true)}>Cancel mission</Button>
            </Stack>
          </Box>}
          {selected.document&&<Box className="ezee-missions__deliverable">
            <Typography variant="subtitle2" fontWeight={800}>{documentLabel} created</Typography>
            {documentLink?
              <Button size="small" component="a" href={documentLink}
                target="_blank" rel="noopener noreferrer">Open {documentLabel}</Button>:
              <Typography variant="body2">{documentLabel} #{selected.document.id}</Typography>}
          </Box>}
          {selected.emailDraft&&<Box className="ezee-missions__deliverable">
            <Typography variant="subtitle2" fontWeight={800}>Email draft — not sent</Typography>
            <Typography variant="caption" display="block">
              {"To: "+(selected.emailDraft.to||"Recipient not selected")}
            </Typography>
            <Typography variant="body2" fontWeight={700} mt={1}>{selected.emailDraft.subject}</Typography>
            <Typography variant="body2" sx={{whiteSpace:"pre-wrap",mt:1}}>{selected.emailDraft.body}</Typography>
            <Button size="small" sx={{mt:1}} onClick={()=>
              navigator.clipboard?.writeText("To: "+(selected.emailDraft.to||"")+
                "\nSubject: "+selected.emailDraft.subject+"\n\n"+selected.emailDraft.body)
            }>Copy draft</Button>
            <Typography variant="caption" color="text.secondary" display="block">
              Use Elite Core's existing Email Composer to review and send. Ezee has not emailed the customer.
            </Typography>
          </Box>}
          <Typography variant="caption" color="text.secondary">{selected.next}</Typography>
        </Stack>}
      </Paper>
    </Box>
    <Dialog open={approvalOpen} onClose={()=>!busy&&setApprovalOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>{"Approve mission step "+selected?.step+"?"}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {selected?.stage==="cart"?
            "Ezee will add this item to the live project cart.":
            "Ezee will generate the "+documentLabel+" from the current cart."}
        </Typography>
        {currentProduct&&isEstimate&&<Box sx={{mt:1,p:1.5,bgcolor:"#f8fafc",borderRadius:1}}>
          <Typography variant="body2" fontWeight={700}>
            {(currentProduct.name||"Product")+" · Qty "+currentProduct.quantity}
          </Typography>
          <Typography variant="caption" display="block">
            {"Colour "+(currentProduct.color||"N/A")+" · Size "+(currentProduct.size||"N/A")}
          </Typography>
          {currentProduct.decoration&&<Typography variant="caption" display="block">
            {"Imprint: "+(currentProduct.decoration.method||"Method missing")+
              " at "+(currentProduct.decoration.location||"Location missing")}
          </Typography>}
          <Typography variant="caption" display="block">
            {"Proposed line total "+money(currentProduct.totalRevenue,currentProduct.currency)}
          </Typography>
        </Box>}
        {isEstimate&&selected?.stage==="estimate"&&<Alert severity="info" sx={{mt:1}}>
          The whole project cart will be used, including existing items. Ezee verifies that the reviewed cart has not changed before Core creates the estimate.
        </Alert>}
        {selected?.requiresFinancialAcknowledgement&&<Box sx={{mt:2}}>
          <Alert severity="warning" sx={{mb:1}}>
            The existing sales order creation workflow may also generate a deposit invoice
            if the customer's payment terms require one. Review the customer and terms first.
          </Alert>
          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
            <Checkbox checked={ackFinancial}
              onChange={event=>setAckFinancial(event.target.checked)}
              inputProps={{"aria-label":"Acknowledge possible deposit invoice"}}/>
            <Typography variant="body2" sx={{pt:1}}>
              I understand that creating this Sales Order may also create a deposit invoice.
            </Typography>
          </Stack>
        </Box>}
        <Typography variant="caption" color="text.secondary" display="block" sx={{mt:1}}>
          This approves one change only. It never sends email.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={()=>{setApprovalOpen(false);setAckFinancial(false);}}>Back</Button>
        <Button variant="contained"
          disabled={busy||(selected?.requiresFinancialAcknowledgement&&!ackFinancial)}
          onClick={()=>void act("approve")}>Approve step</Button>
      </DialogActions>
    </Dialog>
    <Dialog open={cancelOpen} onClose={()=>!busy&&setCancelOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Cancel mission?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Any cart items, sales documents or related deposit invoices already created will remain in Elite Core.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={()=>setCancelOpen(false)}>Keep mission</Button>
        <Button variant="contained" color="error" disabled={busy} onClick={()=>void act("cancel")}>
          Cancel mission
        </Button>
      </DialogActions>
    </Dialog>
  </Box>;
}
